import React, {useEffect, useRef, useState} from 'react';
import mermaid from 'mermaid';

interface MermaidRendererProps {
    chart: string;
}

const MermaidRenderer: React.FC<MermaidRendererProps> = ({chart}) => {
    const [svg, setSvg] = useState<string>('');
    const [error, setError] = useState<string | null>(null);
    const mermaidRef = useRef<HTMLDivElement>(null);

    // Use useMemo to create a stable ID that only changes when the chart content changes
    const uniqueId = React.useMemo(
        () => `mermaid-${Math.random().toString(36).substr(2, 9)}`,
        // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally regenerate the random id whenever the chart content changes
        [chart]
    );

    useEffect(() => {
        mermaid.initialize({
            startOnLoad: true,
            theme: 'dark',
            securityLevel: 'loose',
            fontFamily: 'monospace'
        });

        const renderChart = async () => {
            try {
                setError(null);
                const {svg} = await mermaid.render(uniqueId, chart);
                setSvg(svg);
            } catch (err) {
                console.error('Mermaid rendering error:', err);
                setError('Failed to render diagram. Please check your syntax.');
            }
        };

        renderChart();
    }, [chart, uniqueId]);

    if (error) {
        return (
            <div className="mermaid-error">
                <p>Error: {error}</p>
                <pre>{chart}</pre>
            </div>
        );
    }

    if (!svg) {
        return <div>Loading diagram...</div>;
    }

    return (
        <div
            className="mermaid-diagram"
            ref={mermaidRef}
            dangerouslySetInnerHTML={{__html: svg}}
        />
    );
};

export default MermaidRenderer;
